import axios from "axios";
import { useState, useEffect } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomTooltip from "../../Layout/Tooltip/CustomTooltip";
import { formatSalesValue } from './../../utils/formatUtils';
import { convertToDisplayUnit } from "../../utils/quantityConversion";

const RecipeTable = ({ baseUrl, token, outlet, itemName }) => {
    const navigate = useNavigate();

    const [recipeData, setRecipeData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchRecipeData();
    }, []);


    const handleEdit = (recipe) => {
        navigate(`/menuitemcost?outlet=${outlet}`, {
            state: {
                recipeData: recipe
            }
        });
    };

    const fetchRecipeData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/recipes`, {
                token: token,
                item_name: itemName,
                outlet: outlet
            });
            setRecipeData(response.data);
            setLoading(false);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || "Error fetching data");
            setLoading(false);
        }
    };

    const handleDelete = (recipeId) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to delete this recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={async () => {
                            toast.dismiss('delete-subrecipe-confirmation');
                            setLoading(true);
                            try {
                                await axios.delete(`${baseUrl}/recipe/${recipeId}`, {
                                    data: { token }
                                });
                                await fetchRecipeData();
                                toast.success("Recipe deleted successfully")
                            } catch (err) {
                                console.log(err);
                                toast.error("Error deleting recipe")
                            } finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Delete
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('delete-subrecipe-confirmation')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'delete-recipe-confirmation'
            }
        );
    };

    if (loading) {
        return (
            <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return <div className="text-red-600 text-center">{error}</div>;
    }

    if (recipeData.length === 0) {
        return <div className="text-center mt-10 bg-gray-100 text-gray-700 font-medium p-4 space-y-2 max-w-3xl mx-auto">
            <div>No recipe data available</div>
            <Link to={`/menuitemcost?outlet=${outlet}`}>add?</Link>
        </div>
    }

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            {recipeData.map((recipe) => (
                <div key={recipe.id} className="mb-6">
                    <div className="flex flex-row justify-between items-center my-3">
                        <div className="bg-gray-50 px-4 py-2.5 rounded-md w-4/5 grid grid-cols-3">
                            <div className="flex gap-2">
                                <div className="font-medium text-gray-600">Total Cost Price:</div>
                                <div className="font-semibold">{formatSalesValue(recipe.costprice)}</div>
                            </div>
                            <div className="flex gap-2">
                                <div className="font-medium text-gray-600">Selling Price:</div>
                                <div className="font-semibold">{formatSalesValue(recipe.sellingprice)}</div>
                            </div>
                            <div className="flex gap-2">
                                <div className="font-medium text-gray-600">Cost Percentage:</div>
                                <div className="font-semibold">{Number(recipe.cost_percent)?.toFixed(2)}%</div>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <button
                                onClick={() => handleEdit(recipe)}
                                className="flex items-center gap-1 px-3 py-2 rounded-sm text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md"
                            >
                                <FaEdit className="h-4 w-4 mr-1" /> Edit
                            </button>
                            <button
                                onClick={() => handleDelete(recipe.id)}
                                className="flex items-center gap-1 px-3 py-2 rounded-sm text-sm font-medium bg-rose-100 text-rose-800 hover:bg-rose-200 hover:shadow-md"
                            >
                                <FaTrashAlt className="h-4 w-4 mr-1" /> Delete
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-4">
                        <div>
                            <div className="rounded-md border overflow-x-auto max-h-[500px] overflow-y-auto tiny-scrollbar">
                                <table className="min-w-full divide-y divide-gray-200 text-center">
                                    <thead className="bg-gray-50 sticky top-0 z-20">
                                        <tr>
                                            <th className="px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">Ingredients</th>
                                            <th className="px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">Quantity</th>
                                            <th className="px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">UOM</th>
                                            <th className="px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {recipe.items.map((item, index) => (
                                            <tr key={`item-${index}`}>
                                                <td className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                                                    <CustomTooltip
                                                        content={
                                                            <div className="flex flex-col gap-y-1">
                                                                <span>Rate: {formatSalesValue(item.rate)}</span>
                                                                <span>
                                                                    Unit: {item.unit} ({item.uom})
                                                                </span>
                                                            </div>
                                                        }
                                                    >
                                                        <span className="cursor-pointer hover:underline">
                                                            {item.name}
                                                        </span>
                                                    </CustomTooltip>
                                                </td>
                                                <td className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-600">
                                                    {convertToDisplayUnit(
                                                        item.quantity,
                                                        item.uom,
                                                        item.new_uom,
                                                        item.unit
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-600">{item.new_uom || item.uom}</td>
                                                <td className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">{formatSalesValue(item.cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {recipe.sub_recipes && recipe.sub_recipes.length > 0 && (
                            <div>
                                <div className="rounded-md border overflow-x-auto max-h-[500px] overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 text-center">
                                        <thead className="bg-gray-50 sticky top-0 z-20">
                                            <tr>
                                                <th className="p-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Sub-Recipes</th>
                                                <th className="p-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Quantity</th>
                                                <th className="p-3 text-xs font-medium text-gray-600 uppercase tracking-wider">UOM</th>
                                                <th className="p-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Cost</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {recipe.sub_recipes.map((subRecipe, index) => (
                                                <tr key={`sub-${index}`}>
                                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                                        <CustomTooltip
                                                            content={
                                                                <div className="flex flex-col gap-y-1">
                                                                    <span>Rate: {formatSalesValue(subRecipe.rate)}</span>
                                                                    <span>
                                                                        Unit: {subRecipe.unit} ({subRecipe.uom})
                                                                    </span>
                                                                </div>
                                                            }
                                                        >
                                                            <span className="cursor-pointer hover:underline">
                                                                {subRecipe.name}
                                                            </span>
                                                        </CustomTooltip>
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                                        {convertToDisplayUnit(
                                                            subRecipe.quantity,
                                                            subRecipe.uom,
                                                            subRecipe.new_uom,
                                                            subRecipe.unit
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">{subRecipe.new_uom || subRecipe.uom}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{formatSalesValue(subRecipe.costprice) || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default RecipeTable;